import { useCallback, useMemo } from "react";

import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";
import FeatureGates from "@atlassian/feature-gate-js-client";

export type Cohort =
    | "control"
    | "variant"
    | "variant1"
    | "variant2"
    | "not enrolled";

/**
 * useExperiment
 *
 * Hook that determines to enroll a user into an experiment
 * @returns object
 */
export function useExperiment(
    experimentName: string,
    conditions: boolean[],
    createAnalyticsEvent: CreateUIAnalyticsEvent
): ExperimentEnrollment {
    const enrollmentPreconditionsMet = useMemo(
        () => conditions.filter((condition) => !condition).length === 0,
        [conditions]
    );

    const cohort = useMemo(
        () =>
            FeatureGates.getExperimentValue<Cohort>(
                experimentName,
                "cohort",
                "not enrolled",
                {
                    fireExperimentExposure: false
                }
            ),
        [experimentName]
    );

    const fireExposureEvent = useCallback(
        (attributes: object = {}) => {
            if (enrollmentPreconditionsMet) {
                FeatureGates.manuallyLogExperimentExposure(experimentName);

                // fire manual exposure event.
                createAnalyticsEvent &&
                    createAnalyticsEvent({
                        source: `experiment:${experimentName}`,
                        actionSubject: "user",
                        action: "enrolled",
                        actionSubjectId: `experiment:${experimentName}`,
                        attributes: {
                            cohort: cohort,
                            page_url: `${window.location.origin}/${window.location.pathname} `,
                            page_path: window.location.pathname,
                            ...attributes
                        },
                        eventType: "track"
                    }).fire();
            } else {
                console.warn(
                    "cannot fire exposure event. enrollment preconditions were not met."
                );
            }
        },
        [enrollmentPreconditionsMet]
    );

    return {
        enrollmentPreconditionsMet,
        cohort: enrollmentPreconditionsMet ? cohort : "not enrolled",
        fireExposureEvent
    };
}

export type ExperimentEnrollment = {
    enrollmentPreconditionsMet: boolean;
    cohort: Cohort;
    fireExposureEvent: (attributes?: object) => void;
};
